
import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const SERVICES_OPTIONS = [
  'Strona premium',
  'SEO',
  'Agenci AI',
  'Wizytówka Google',
  '360°',
  'Dron/Fotografia',
  'Pakiet kompleksowy',
];

const BUDGET_OPTIONS = ['< 5 tys. zł', '5–15 tys. zł', '15–40 tys. zł', '40 tys. zł+'];

const CONTACT_INFO = [
  {
    icon: 'ri-phone-line',
    label: 'Telefon',
    value: '+48 600 601 093',
    href: 'tel:+48600601093',
    color: '#00ff88',
  },
  {
    icon: 'ri-mail-line',
    label: 'E-mail',
    value: 'kontakt@orsonet.pl',
    href: 'mailto:kontakt@orsonet.pl',
    color: '#00d4ff',
  },
  {
    icon: 'ri-time-line',
    label: 'Godziny pracy',
    value: 'Pon–Pt: 9:00–18:00',
    href: '#',
    color: '#ff6b9d',
  },
];

function AnimatedSection({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

export default function KontaktPage() {
  const [formData, setFormData] = useState({
    imie: '',
    email: '',
    telefon: '',
    usluga: '',
    opis: '',
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'opis') setCharCount(value.length);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setStatus('sending');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d6jfprec9mmd5omn5lr0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setFormData({ imie: '', email: '', telefon: '', usluga: '', opis: '' });
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 50% 0%, rgba(0,212,255,0.08) 0%, transparent 60%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0,212,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.4) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 text-center">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00d4ff]/30 bg-[#00d4ff]/5 backdrop-blur-sm mb-8">
              <span className="w-2 h-2 rounded-full bg-[#00d4ff] animate-pulse" />
              <span className="text-[#00d4ff] text-xs font-semibold tracking-widest uppercase">Bezpłatna konsultacja</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6">
              Skontaktuj się<br />
              <span
                className="text-transparent bg-clip-text"
                style={{ backgroundImage: 'linear-gradient(135deg, #00d4ff, #00ff88)' }}
              >
                z nami
              </span>
            </h1>
            <p className="text-lg text-white/50 max-w-xl mx-auto leading-relaxed">
              Opowiedz nam o swoim projekcie. Odpiszemy w ciągu 24 godzin z konkretną propozycją.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Main content */}
      <section className="pb-32">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">

            {/* Left: info */}
            <div className="lg:col-span-2 space-y-6">
              <AnimatedSection delay={0.1}>
                <div className="p-8 rounded-3xl border border-white/10 bg-white/[0.03]">
                  <h2 className="text-xl font-bold text-white mb-6">Informacje kontaktowe</h2>
                  <div className="space-y-5">
                    {CONTACT_INFO.map((item) => (
                      <a
                        key={item.label}
                        href={item.href}
                        rel="nofollow"
                        className="flex items-start gap-4 group cursor-pointer"
                      >
                        <div
                          className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                          style={{
                            background: `${item.color}15`,
                            border: `1px solid ${item.color}30`,
                          }}
                        >
                          <i className={`${item.icon} text-base`} style={{ color: item.color }} />
                        </div>
                        <div>
                          <p className="text-white/35 text-xs font-medium uppercase tracking-wider mb-0.5">{item.label}</p>
                          <p className="text-white/80 text-sm font-medium group-hover:text-white transition-colors">{item.value}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.2}>
                <div className="p-8 rounded-3xl border border-white/10 bg-white/[0.03]">
                  <h3 className="text-base font-bold text-white mb-5">Social media</h3>
                  <div className="flex gap-3">
                    {[
                      { icon: 'ri-facebook-fill', href: '#', color: '#00d4ff' },
                      { icon: 'ri-linkedin-fill', href: '#', color: '#00d4ff' },
                      { icon: 'ri-instagram-line', href: '#', color: '#ff6b9d' },
                      { icon: 'ri-youtube-line', href: '#', color: '#ff6b9d' },
                    ].map((s) => (
                      <a
                        key={s.icon}
                        href={s.href}
                        rel="nofollow"
                        className="w-11 h-11 rounded-xl border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:border-white/30 transition-all cursor-pointer hover:scale-110"
                      >
                        <i className={s.icon} />
                      </a>
                    ))}
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.3}>
                <div
                  className="p-8 rounded-3xl overflow-hidden relative"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0,212,255,0.08) 0%, rgba(0,255,136,0.05) 100%)',
                    border: '1px solid rgba(0,212,255,0.2)',
                  }}
                >
                  <div className="absolute top-0 right-0 w-40 h-40 rounded-full pointer-events-none"
                    style={{ background: 'radial-gradient(circle, rgba(0,212,255,0.1) 0%, transparent 70%)', filter: 'blur(20px)' }}
                  />
                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                      style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)' }}>
                      <i className="ri-file-download-line text-2xl text-[#00d4ff]" />
                    </div>
                    <h3 className="text-base font-bold text-white mb-2">Bezpłatny PDF</h3>
                    <p className="text-white/45 text-sm leading-relaxed mb-5">
                      Lista kontrolna: Strona, która sprzedaje + SEO. 23 punkty do sprawdzenia.
                    </p>
                    <button className="inline-flex items-center gap-2 text-[#00d4ff] text-sm font-semibold hover:gap-3 transition-all cursor-pointer">
                      Pobierz bezpłatnie
                      <i className="ri-arrow-right-line" />
                    </button>
                  </div>
                </div>
              </AnimatedSection>
            </div>

            {/* Right: form */}
            <div className="lg:col-span-3">
              <AnimatedSection delay={0.15}>
                <div className="p-8 md:p-10 rounded-3xl border border-white/10 bg-white/[0.03]">
                  <div className="mb-8">
                    <h2 className="text-2xl font-black text-white mb-2">Napisz do nas</h2>
                    <p className="text-white/40 text-sm">Wypełnij formularz, a odezwiemy się w ciągu 24 godzin.</p>
                  </div>

                  {status === 'success' ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div
                        className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
                        style={{ background: 'rgba(0,255,136,0.1)', border: '2px solid rgba(0,255,136,0.3)' }}
                      >
                        <i className="ri-check-line text-4xl text-[#00ff88]" />
                      </div>
                      <h3 className="text-2xl font-black text-white mb-3">Wiadomość wysłana!</h3>
                      <p className="text-white/50 text-sm max-w-sm leading-relaxed mb-8">
                        Dziękujemy za kontakt. Odpiszemy w ciągu 24 godzin roboczych.
                      </p>
                      <button
                        onClick={() => setStatus('idle')}
                        className="px-8 py-3 rounded-xl font-bold text-black text-sm cursor-pointer hover:scale-105 transition-transform"
                        style={{ background: 'linear-gradient(135deg, #00d4ff, #00b8e6)' }}
                      >
                        Wyślij kolejną wiadomość
                      </button>
                    </div>
                  ) : (
                    <form
                      data-readdy-form
                      id="kontakt-form"
                      onSubmit={handleSubmit}
                      className="space-y-5"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">
                            Imię i nazwisko <span className="text-[#00d4ff]">*</span>
                          </label>
                          <input
                            type="text"
                            name="imie"
                            required
                            value={formData.imie}
                            onChange={handleChange}
                            placeholder="Jan Kowalski"
                            className="w-full px-4 py-3.5 rounded-xl text-sm text-white placeholder-white/25 outline-none transition-all duration-300 focus:border-[#00d4ff]/50"
                            style={{
                              background: 'rgba(255,255,255,0.04)',
                              border: '1px solid rgba(255,255,255,0.1)',
                            }}
                            onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(0,212,255,0.4)'; e.currentTarget.style.background = 'rgba(0,212,255,0.04)'; }}
                            onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                          />
                        </div>
                        <div>
                          <label className="block text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">
                            E-mail <span className="text-[#00d4ff]">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="jan@firma.pl"
                            className="w-full px-4 py-3.5 rounded-xl text-sm text-white placeholder-white/25 outline-none transition-all duration-300"
                            style={{
                              background: 'rgba(255,255,255,0.04)',
                              border: '1px solid rgba(255,255,255,0.1)',
                            }}
                            onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(0,212,255,0.4)'; e.currentTarget.style.background = 'rgba(0,212,255,0.04)'; }}
                            onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">
                            Telefon
                          </label>
                          <input
                            type="tel"
                            name="telefon"
                            value={formData.telefon}
                            onChange={handleChange}
                            placeholder="+48 000 000 000"
                            className="w-full px-4 py-3.5 rounded-xl text-sm text-white placeholder-white/25 outline-none transition-all duration-300"
                            style={{
                              background: 'rgba(255,255,255,0.04)',
                              border: '1px solid rgba(255,255,255,0.1)',
                            }}
                            onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(0,212,255,0.4)'; e.currentTarget.style.background = 'rgba(0,212,255,0.04)'; }}
                            onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                          />
                        </div>
                        <div>
                          <label className="block text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">
                            Interesująca usługa <span className="text-[#00d4ff]">*</span>
                          </label>
                          <select
                            name="usluga"
                            required
                            value={formData.usluga}
                            onChange={handleChange}
                            className="w-full px-4 py-3.5 rounded-xl text-sm text-white outline-none transition-all duration-300 cursor-pointer appearance-none"
                            style={{
                              background: 'rgba(255,255,255,0.04)',
                              border: '1px solid rgba(255,255,255,0.1)',
                              color: formData.usluga ? 'white' : 'rgba(255,255,255,0.25)',
                            }}
                            onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(0,212,255,0.4)'; }}
                            onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                          >
                            <option value="" disabled style={{ background: '#1a1a1a' }}>Wybierz usługę</option>
                            {SERVICES_OPTIONS.map((opt) => (
                              <option key={opt} value={opt} style={{ background: '#1a1a1a', color: 'white' }}>{opt}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-white/50 text-xs font-semibold uppercase tracking-wider">
                            Opis projektu <span className="text-[#00d4ff]">*</span>
                          </label>
                          <span
                            className="text-xs font-medium"
                            style={{ color: charCount > 500 ? '#ff4444' : charCount > 400 ? '#ff6b9d' : 'rgba(255,255,255,0.3)' }}
                          >
                            {charCount}/500
                          </span>
                        </div>
                        <textarea
                          name="opis"
                          required
                          value={formData.opis}
                          onChange={handleChange}
                          placeholder="Opisz swój projekt, branżę, cele i oczekiwania..."
                          rows={5}
                          maxLength={500}
                          className="w-full px-4 py-3.5 rounded-xl text-sm text-white placeholder-white/25 outline-none transition-all duration-300 resize-none"
                          style={{
                            background: 'rgba(255,255,255,0.04)',
                            border: charCount > 500 ? '1px solid rgba(255,68,68,0.5)' : '1px solid rgba(255,255,255,0.1)',
                          }}
                          onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(0,212,255,0.4)'; e.currentTarget.style.background = 'rgba(0,212,255,0.04)'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = charCount > 500 ? 'rgba(255,68,68,0.5)' : 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                        />
                        {charCount > 500 && (
                          <p className="text-red-400 text-xs mt-1">Przekroczono limit 500 znaków.</p>
                        )}
                      </div>

                      {status === 'error' && (
                        <div className="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                          <i className="ri-error-warning-line text-red-400" />
                          <p className="text-red-400 text-sm">Wystąpił błąd. Spróbuj ponownie lub napisz bezpośrednio na kontakt@orsonet.pl</p>
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={status === 'sending' || charCount > 500}
                        className="w-full py-4 rounded-xl font-bold text-black text-base cursor-pointer hover:scale-[1.02] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100 shadow-[0_0_30px_rgba(0,212,255,0.25)] whitespace-nowrap"
                        style={{ background: 'linear-gradient(135deg, #00d4ff, #00b8e6)' }}
                      >
                        {status === 'sending' ? (
                          <span className="flex items-center justify-center gap-2">
                            <i className="ri-loader-4-line animate-spin" />
                            Wysyłanie...
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            <i className="ri-send-plane-line" />
                            Wyślij wiadomość
                          </span>
                        )}
                      </button>

                      <p className="text-white/25 text-xs text-center leading-relaxed">
                        Wysyłając formularz, akceptujesz{' '}
                        <a href="#" rel="nofollow" className="text-white/40 hover:text-white/70 transition-colors cursor-pointer">politykę prywatności</a>.
                        Nie wysyłamy spamu.
                      </p>
                    </form>
                  )}
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Map section */}
      <section className="pb-32">
        <div className="max-w-[1400px] mx-auto px-6">
          <AnimatedSection>
            <div className="rounded-3xl overflow-hidden border border-white/10" style={{ height: '400px' }}>
              <iframe
                title="Orsonet - Warszawa"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d156388.35438582!2d20.921787!3d52.229676!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x471ecc669a869f01%3A0x72f0be2a688bae3f!2sWarszawa!5e0!3m2!1spl!2spl!4v1700000000000!5m2!1spl!2spl"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) brightness(0.85) contrast(1.1)' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />

      {/* Sticky CTA */}
      <Link
        to="/kontakt"
        className="fixed bottom-8 right-8 z-50 flex items-center gap-2 px-5 py-3.5 rounded-full font-bold text-black text-sm whitespace-nowrap cursor-pointer hover:scale-110 transition-transform shadow-[0_0_30px_rgba(0,212,255,0.5)]"
        style={{ background: 'linear-gradient(135deg, #00d4ff, #00b8e6)' }}
      >
        <i className="ri-calendar-check-line text-lg" />
        <span className="hidden sm:inline">Umów konsultację</span>
      </Link>
    </div>
  );
}
